import { z } from 'zod';
import {
  WorkspaceId,
  ArtistId,
  GigId,
  LiveSessionId,
  SongId,
  InteractionId,
  AudienceSessionId,
  DuplicateGroupId,
} from '../ids.js';
import { IsoTimestamp, Version, SourceCode, IdempotencyKey } from '../primitives.js';
import {
  InteractionType,
  ModerationState,
  PerformerStatus,
  PublicStatus,
} from '../enums.js';

/** §27.10 AudienceSession — anonymous, ephemeral. Only a token hash is stored (§15.3, §25.1). */
export const AudienceSession = z.object({
  audienceSessionId: AudienceSessionId,
  tokenHash: z.string().min(1),
  firstGigId: GigId,
  createdAt: IsoTimestamp,
  lastSeenAt: IsoTimestamp,
  expiresAt: IsoTimestamp,
  blockedGigIds: z.array(GigId).default([]),
});
export type AudienceSession = z.infer<typeof AudienceSession>;

/** §27.11 Interaction base fields shared by every interaction type. */
export const InteractionBase = z.object({
  interactionId: InteractionId,
  interactionType: InteractionType,
  workspaceId: WorkspaceId,
  artistId: ArtistId,
  gigId: GigId,
  liveSessionId: LiveSessionId.nullable().default(null),
  audienceSessionId: AudienceSessionId,
  sourceCode: SourceCode.nullable().default(null),
  idempotencyKey: IdempotencyKey,
  moderationState: ModerationState.default('clean'),
  performerStatus: PerformerStatus.default('new'),
  publicStatus: PublicStatus.default('received'),
  submittedAt: IsoTimestamp,
  updatedAt: IsoTimestamp,
  version: Version,
});
export type InteractionBase = z.infer<typeof InteractionBase>;

/** §27.12 SongRequest. */
export const SongRequest = InteractionBase.extend({
  interactionType: z.literal('song_request'),
  songId: SongId,
  nickname: z.string().max(30).nullable().default(null),
  note: z.string().max(280).nullable().default(null),
  duplicateGroupId: DuplicateGroupId.nullable().default(null),
  cooldownSnapshot: z.record(z.string(), z.unknown()).default({}),
});
export type SongRequest = z.infer<typeof SongRequest>;

/** §27.13 FreeformSuggestion. Abuse signals are stored separately from content (§19.3). */
export const FreeformSuggestion = InteractionBase.extend({
  interactionType: z.literal('freeform'),
  category: z.string().max(40).nullable().default(null),
  message: z.string().min(1).max(280),
  nickname: z.string().max(30).nullable().default(null),
  relatedSongTitle: z.string().max(200).nullable().default(null),
  abuseSignals: z.array(z.string()).default([]),
});
export type FreeformSuggestion = z.infer<typeof FreeformSuggestion>;

/** §18.5 Duplicate request group projection for the performer. */
export const DuplicateGroup = z.object({
  duplicateGroupId: DuplicateGroupId,
  gigId: GigId,
  songId: SongId,
  totalRequests: z.number().int().nonnegative().default(0),
  distinctSessions: z.number().int().nonnegative().default(0),
  mostRecentAt: IsoTimestamp,
  performerStatus: PerformerStatus.default('new'),
  version: Version,
});
export type DuplicateGroup = z.infer<typeof DuplicateGroup>;
